<script setup lang="ts">
import type { ListboxChangeEvent } from 'primevue/listbox';
import { useTranslation } from 'i18next-vue';
import {
  cloneDeep,
  isEqual,
} from 'lodash-es';
import {
  computed,
  ref,
  watch,
} from 'vue';

import type { IFormPlainDataListboxPropsChangeState } from '@modules/forms/types';
import type {
  ISmartFilterColumn,
  ISmartFilterColumnDataCompare,
  ISmartFilterColumnDataDate,
  ISmartFilterColumnDataPayload,
  ISmartFilterEmitsDataValues,
} from '@modules/smart-filters/types';
import VLoader from '@components/loader/VLoader.vue';
import VSplitter from '@components/splitter/VSplitter/VSplitter.vue';
import { useDevice } from '@modules/core/composables/device';
import VFormPlainDataListbox from '@modules/forms/components/inputs/VFormPlainDataListbox/VFormPlainDataListbox.vue';
import VSmartFilterColumn from '@modules/smart-filters/components/VSmartFilterColumn/VSmartFilterColumn.vue';
import VSmartFilterCompare from '@modules/smart-filters/components/VSmartFilterCompare/VSmartFilterCompare.vue';
import VSmartFilterDateRange from '@modules/smart-filters/components/VSmartFilterDate/VSmartFilterDateRange.vue';
import {
  DEFAULT_SMART_FILTER_CHECKBOX_DATA,
  DEFAULT_SMART_FILTER_COMPARE_DATA,
  DEFAULT_SMART_FILTER_DATE_DATA,
  RESPONSE_DATE_FORMAT,
} from '@modules/smart-filters/constants';
import {
  isSmartFilterCheckboxColumnId,
  isSmartFilterCheckboxColumnIdData,
  isSmartFilterCompareColumnId,
  isSmartFilterCompareColumnIdData,
  isSmartFilterDateColumnId,
  isSmartFilterDateColumnIdData,
} from '@modules/smart-filters/utils';
import { QA_TABLES } from '@modules/tables/constants';
import { formatDate } from '@vueuse/core';

import type {
  ISmartFilterEmits,
  ISmartFilterProps,
} from './types';

const props = defineProps<ISmartFilterProps>();
const emit = defineEmits<ISmartFilterEmits>();

const { t } = useTranslation();
const { isDesktop } = useDevice();
const resetCounter = ref(0);

function getColumnByName(columnName?: string): ISmartFilterColumn | undefined {
  return props.columns.find((column) => column.id === columnName);
}

const selectedColumn = computed<ISmartFilterColumn | undefined>(
  () => getColumnByName(props.selectedColumnId),
);

function getColumnDataForReset(columnName: string): ISmartFilterColumnDataPayload {
  if (isSmartFilterCheckboxColumnId(getColumnByName(columnName))) {
    return { ...DEFAULT_SMART_FILTER_CHECKBOX_DATA };
  }
  if (isSmartFilterCompareColumnId(getColumnByName(columnName))) {
    return cloneDeep(DEFAULT_SMART_FILTER_COMPARE_DATA);
  }
  if (isSmartFilterDateColumnId(getColumnByName(columnName))) {
    return { ...DEFAULT_SMART_FILTER_DATE_DATA };
  }

  throw new Error('Unexpected column id');
}

function emitDataChange(data: Omit<ISmartFilterEmitsDataValues, 'columnName'>): void {
  if (props.selectedColumnId) {
    emit('data-change', {
      ...data,
      columnName: props.selectedColumnId,
    });
  }
}

function clearColumnData(columnName: string): void {
  const isCheckboxColumn = isSmartFilterCheckboxColumnId(getColumnByName(columnName));

  if (columnName === props.selectedColumnId && isCheckboxColumn) {
    resetCounter.value += 1;
    return;
  }

  emit('data-change', {
    columnName,
    data: getColumnDataForReset(columnName),
    needToAdd: !isCheckboxColumn,
    isEmpty: true,
  });
}

function handleSelectionColumn({ value }: ListboxChangeEvent): void {
  emit('column-change', value);
}

function handleCompareChange(values: ISmartFilterColumnDataCompare): void {
  emitDataChange({
    data: values,
    needToAdd: true,
    isEmpty: isEqual(values, DEFAULT_SMART_FILTER_COMPARE_DATA),
  });
}

function handleCheckboxChange(state: IFormPlainDataListboxPropsChangeState): void {
  const { check, values, ...data } = state;

  emitDataChange({
    data,
    needToAdd: check,
    isEmpty: values.length === 0,
  });
}

function handleDateChange(data: ISmartFilterColumnDataDate): void {
  emitDataChange({
    data: {
      start: data.start ? formatDate(data.start, RESPONSE_DATE_FORMAT) : null,
      end: data.end ? formatDate(data.end, RESPONSE_DATE_FORMAT) : null,
    },
    needToAdd: true,
    isEmpty: isEqual(data, DEFAULT_SMART_FILTER_DATE_DATA),
  });
}

function onOpenRightSide(column: ISmartFilterColumn, open: (title?: string) => void): void {
  if (props.selectedColumnId === column.id) {
    open(props.translationPath ? t(`${props.translationPath}.${column.id}`) : column.label);
  } else {
    const unwatch = watch(() => props.selectedColumnId, () => {
      open(props.translationPath ? t(`${props.translationPath}.${props.selectedColumnId}`) : column.label);
      unwatch();
    });
  }
}
</script>

<template>
  <VSplitter v-data-qa-auto-id="'VSmartFilter'"
    :splitter-root-pt="{
      root: { class: 'min-h-[360px]' },
    }"
    :left-prime-vue-props="{ size: 30, minSize: 20 }"
    :right-prime-vue-props="{ size: 70, minSize: 50 }"
  >
    <template #left="{ openItem }">
      <PListbox
        :model-value="selectedColumnId"
        :options="columns"
        option-value="id"
        scroll-height="360px"
        class="w-full !border-0"
        :pt="{ option: { class: '!p-0' } }"
        @change="handleSelectionColumn"
      >
        <template #option="{ option }">
          <div
            v-data-test="{ el: QA_TABLES.filterColumn, columnName: option.id }"
            class="relative flex min-h-[42px] w-full items-center !py-2 !pl-3 !pr-2"
            @click="onOpenRightSide(option, openItem)"
          >
            <VSmartFilterColumn
              :column="option"
              :translation-path="translationPath"
              :is-supervision="isSupervision"
              @clear-column-data="clearColumnData(option.id)"
            />
          </div>
        </template>
      </PListbox>
    </template>
    <template #right>
      <VLoader
        v-if="selectedColumn"
        :loading="isLoadingColumn"
        mode="structural-immediate"
        min-width="100%"
        min-height="100%"
      >
        <VFormPlainDataListbox
          v-if="isSmartFilterCheckboxColumnIdData(selectedColumn, data)"
          :data="data"
          hide-errors
          :no-border="isDesktop"
          is-full-width
          :sort-after-change-item="sortCheckboxesAfterChangeItem"
          :disabled="isSavingColumn || isSupervision"
          :disable-checkbox-filter="selectedColumn.disableCheckboxFilter"
          :translation-path="selectedColumn.translationPath"
          :translation-path-for-falsy-value="selectedColumn.translationPathForFalsyValue"
          :translation-path-for-truthy-value="selectedColumn.translationPathForTruthyValue"
          :reset-counter="resetCounter"
          @change="handleCheckboxChange"
        />
        <VSmartFilterCompare
          v-else-if="isSmartFilterCompareColumnIdData(selectedColumn, data)"
          :data="data"
          :is-supervision="isSupervision"
          class="lg:p-3 lg:pr-2"
          @change="handleCompareChange"
        />
        <VSmartFilterDateRange
          v-else-if="isSmartFilterDateColumnIdData(selectedColumn, data)"
          :data="data"
          :is-supervision="isSupervision"
          class="lg:p-3"
          @change="handleDateChange"
        />
      </VLoader>
    </template>
  </VSplitter>
</template>
